import React from 'react';
import styled from 'styled-components';
import { message, Select } from 'antd';
import { get } from 'lodash';
import { Profile } from '@conviva/portal-graphql-schema';
import { useMutation } from '@apollo/react-hooks';
import { UPDATE_DEFAULT_APP } from '@api/graphql/profile/applications';
import { CS_ROLE_NAME } from '@utils/constants';

const Product = {
  ei: 'VIDEO',
  ai: 'ADS',
  app: 'ECO',
  p4: 'Pulse 4.0',
  ci: 'AUDIENCE',
  precision: 'Precision',
  si: 'SOCIAL',
  vi: 'AUDIENCE',
  plm: 'App Manager',
  touchstone: 'Touchstone',
  community: 'Community',
  mds: 'Metadata Service',
  advi: 'ADS',
};

const appNameMapping = Object.fromEntries(Object.entries(Product));

const StyledWrapper = styled.span`
  button {
    background-color: transparent !important;
    font-weight: 300 !important;
    padding-left: 0 !important;
  }
`;

// SI use special access control method.
const checkAppAccessByCookie = (appName: string) => {
  const tokenName =
    location.origin === 'https://pulse.conviva.com' ? 'accessToken' : 'qe2AccessToken';

  return document.cookie.split(';').some((c) => {
    const [k, v] = c.split('=');
    if (k.trim() !== tokenName) {
      return false;
    }
    const { applications = [] } = JSON.parse(
      atob(JSON.parse(decodeURIComponent(v)).accessToken.split('.')[1]),
    );
    return applications.includes(appName);
  });
};

const getApplicationMenu = (profile: Profile) => {
  const customer: any = profile.customer;
  const items = [{ value: 'ei', label: Product.ei }];
  if (customer.adInsightsEnabled && profile.role !== CS_ROLE_NAME) {
    items.push({ value: 'ai', label: Product.ai });
  }
  if (customer.precisionEnabled && profile.role !== CS_ROLE_NAME) {
    items.push({ value: 'precision', label: Product.precision });
  }
  items.push({ value: 'touchstone', label: Product.touchstone });
  items.push({ value: 'plm', label: Product.plm });
  if (customer.appEnabled) {
    items.push({ value: 'app', label: Product.app });
  }

  const hasSocialAccess = checkAppAccessByCookie('Social Insights');
  const hasCIAccess = profile.ciEnabled;
  const hasAdvIAccess = profile.aiEnabled;
  const hasVIAccess = hasSocialAccess && hasCIAccess;

  if (hasVIAccess) {
    items.push({ value: 'vi', label: Product.vi });
    items.push({ value: 'si', label: Product.si });
  } else if (hasCIAccess) {
    items.push({ value: 'ci', label: Product.ci });
  } else if (hasSocialAccess) {
    items.push({ value: 'si', label: Product.si });
  }
  if (hasAdvIAccess) {
    items.push({ value: 'advi', label: Product.advi });
  }

  return items;
};

interface IApplicationDropdownProps {
  profileId: number;
  app: string;
  profile: Profile;
}

const ApplicationDropdown = ({ profileId, app, profile }: IApplicationDropdownProps) => {
  const [updateDefaultApp] = useMutation(UPDATE_DEFAULT_APP, {
    onCompleted: (data) =>
      message.success(
        `You have successfully set your default application to "${appNameMapping[get(data, 'updateDefaultApp.defaultApp.app')]}".`,
      ),
  });

  let myApp = app;
  if (profile.role === CS_ROLE_NAME) {
    if (['ai', 'precision'].includes(app)) {
      myApp = 'ei';
    }
  }

  const onChange = React.useCallback(
    (key) => {
      updateDefaultApp({
        variables: {
          profileId,
          payload: {
            app: key,
          },
        },
      });
    },
    [updateDefaultApp, profileId],
  );

  return (
    <StyledWrapper>
      <Select
        options={getApplicationMenu(profile)}
        value={myApp}
        onChange={onChange}
        bordered={false}
        dropdownStyle={{ minWidth: 200 }}
      />
    </StyledWrapper>
  );
};

export default ApplicationDropdown;
